import React, { useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth, API } from '../../App';
import { Bell, Package, Store, TrendingDown } from 'lucide-react';

const playNotificationSound = () => {
    try {
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();
        
        oscillator.type = 'sine';
        // A nice pleasant chime (two notes: C6 then E6)
        
        // Note 1
        oscillator.frequency.setValueAtTime(1046.50, audioCtx.currentTime); // C6
        oscillator.frequency.setValueAtTime(1318.51, audioCtx.currentTime + 0.1); // E6
        
        gainNode.gain.setValueAtTime(0, audioCtx.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.3, audioCtx.currentTime + 0.05);
        gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.5);
        
        oscillator.connect(gainNode);
        gainNode.connect(audioCtx.destination);
        
        oscillator.start();
        oscillator.stop(audioCtx.currentTime + 0.5);
    } catch(e) {
        console.error("Audio playback failed", e);
    }
};

const getIconForType = (type) => {
    switch(type) {
        case 'new_order': return <Package className="w-5 h-5 text-indigo-500" />;
        case 'new_vendor': return <Store className="w-5 h-5 text-emerald-500" />;
        case 'low_stock': return <TrendingDown className="w-5 h-5 text-amber-500" />;
        case 'new_ticket': return <Bell className="w-5 h-5 text-blue-500" />;
        case 'new_expert_visit': return <Bell className="w-5 h-5 text-purple-500" />;
        default: return <Bell className="w-5 h-5 text-primary" />;
    }
};

const GlobalNotifier = ({ role }) => {
    const { token, user } = useAuth();
    const seenIdsRef = useRef(new Set()); 

    useEffect(() => {
        if ('Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission();
        }
    }, []);

    useEffect(() => {
        if (!token || !user) return;

        let interval;
        const fetchNotifications = async () => {
            try {
                const endpointPrefix = role === 'admin' ? '/api/admin' : '/api/vendor';
                
                let settingsFlag = {};
                if (role === 'admin') {
                    const resParams = await axios.get(`${API}/api/admin/platform-settings`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    settingsFlag = resParams.data?.notification_settings || {};
                } else if (role === 'vendor') {
                    settingsFlag = {};
                }

                const res = await axios.get(`${API}${endpointPrefix}/notifications/unread`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                
                let notifications = res.data;
                if (!notifications || notifications.length === 0) return;

                // Filter by settings
                if (role === 'admin') {
                    notifications = notifications.filter(notif => {
                        if (notif.type === 'new_order' && settingsFlag.newOrders === false) return false;
                        if (notif.type === 'new_vendor' && settingsFlag.newVendors === false) return false;
                        if (notif.type === 'low_stock' && settingsFlag.lowStock === false) return false;
                        if (notif.type === 'new_ticket' && settingsFlag.tickets === false) return false;
                        if (notif.type === 'new_expert_visit' && settingsFlag.expertVisits === false) return false;
                        return true;
                    });
                }

                if (notifications.length === 0) return;
                
                const newIds = [];
                const unseen = [];
                
                notifications.forEach(notif => {
                    if (!seenIdsRef.current.has(notif.id)) {
                        unseen.push(notif);
                        seenIdsRef.current.add(notif.id);
                        newIds.push(notif.id);
                    }
                });
                
                if (unseen.length > 0) {
                    // Play custom sound or fallback
                    if (user.notification_sound_url) {
                        try {
                            const audio = new Audio(API + user.notification_sound_url);
                            audio.play().catch(e => {
                                console.log("Custom audio play blocked or failed. Playing fallback.");
                                playNotificationSound();
                            });
                        } catch (e) {
                            playNotificationSound();
                        }
                    } else {
                        playNotificationSound();
                    }
                    
                    unseen.forEach(notif => {
                        toast(notif.title || 'New Notification', {
                            description: notif.message,
                            duration: 8000, 
                            icon: getIconForType(notif.type),
                            action: notif.link ? {
                                label: 'View',
                                onClick: () => window.location.href = notif.link
                            } : undefined
                        });

                        if ('Notification' in window && Notification.permission === 'granted') {
                            const sysNotif = new Notification(notif.title || 'New Notification', {
                                body: notif.message,
                                icon: '/favicon.ico'
                            });
                            if (notif.link) {
                                sysNotif.onclick = () => {
                                    window.open(notif.link, '_blank');
                                };
                            }
                        }
                    });
                    
                    if (newIds.length > 0) {
                        await axios.post(`${API}${endpointPrefix}/notifications/mark-read`, {
                            notification_ids: newIds
                        }, {
                            headers: { Authorization: `Bearer ${token}` }
                        });
                    }
                }
            } catch (err) {
                // silently fail polling
            }
        };

        setTimeout(fetchNotifications, 2000);
        interval = setInterval(fetchNotifications, 15000);

        return () => clearInterval(interval);
    }, [token, user, role]);

    return null; 
};

export default GlobalNotifier;
